import { inject, observer } from "mobx-react";
import React, { useEffect, useState } from "react";
import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Label,
  Switch,
  useToast,
} from "randmarcomps";
import { useManufacturer } from "../../contexts/WithManufacturer";
import { Product } from "../../stores/product/Product";
import { RootStore } from "../../stores/rootStore";
import CenteredLoader from "../util/CenteredLoader";

interface IProps {
  store?: RootStore;
  product: Product;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const DiscontinueProduct: React.FC<IProps> = ({ store, product, open, onOpenChange }) => {
  const { applicationId } = useManufacturer();
  const [loading, setLoading] = useState(false);
  const [cancelAll, setCancelAll] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    if (!open) return;
    setCancelAll(false);
  }, [open]);

  const discontinueProduct = async () => {
    setLoading(true);

    const { productStore } = store!;

    let success = false;

    if (cancelAll)
      success = await productStore.discontinueProduct(applicationId, product.randmarSKU || "");
    else
      success = await productStore.setProductProperty(applicationId, product.randmarSKU || "", "State", "Discontinued");

    if (success) {
      toast({
        variant: "success",
        title: "Success",
        description: `Product ${product.mpn || ""} discontinued.`,
      });
    } else {
      toast({
        variant: "destructive",
        title: "Error",
        description: `Product ${product.mpn || ""} could not be discontinued...`,
      });
    }

    onOpenChange(false);
    await productStore.getProduct(applicationId, product.randmarSKU || "");

    setLoading(false);
  };

  const handleCancel = () => {
    if (loading) return;
    onOpenChange(false);
  };

  return (
    <Dialog
      modal
      open={open}
      onOpenChange={(nextOpen) => {
        if (loading) return;
        onOpenChange(nextOpen);
      }}
    >
      <DialogContent
        className="z-[10001]"
        overlayClassName="z-[10000]"
        onInteractOutside={(event) => {
          const eventType = event.detail?.originalEvent?.type;
          if (eventType === "focusin") {
            event.preventDefault();
          }
        }}
      >
        <DialogHeader>
          <DialogTitle>Discontinue {product.mpn}</DialogTitle>
        </DialogHeader>
        {loading ? (
          <CenteredLoader text="Please wait…" variant="plain" className="min-h-[240px]" />
        ) : (
          <>
            <div className="py-4 text-center">
              <p>Also cancel open purchase orders and reseller orders?</p>
              <div className="flex items-center justify-center space-x-2 mt-4">
                <Switch id="cancel-orders" checked={cancelAll} onCheckedChange={setCancelAll} />
                <Label htmlFor="cancel-orders">Cancel open orders</Label>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={handleCancel}>
                Cancel
              </Button>
              <Button onClick={discontinueProduct}>Confirm</Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default inject("store")(observer(DiscontinueProduct));
